import { useCallback, useEffect, useState } from "react";
import { MapPin, Plus, RefreshCw, Trash2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

const inputClass = "w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100";

export default function EmployeeTerritoryGps({ onTerritoriesLoaded }) {
  const [settings, setSettings] = useState({ territories: [], field_locations: [], office: {} });
  const [locationTree, setLocationTree] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [territory, setTerritory] = useState("");
  const [draft, setDraft] = useState({
    name: "", state_id: "", city_id: "", area_id: "",
    territory: "", latitude: "", longitude: "", radius_km: "0.1",
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/admin/attendance/settings");
      const treeResponse = await api.get("/locations/tree");
      setLocationTree(Array.isArray(treeResponse.data) ? treeResponse.data : []);
      const next = {
        territories: Array.isArray(data.territories) ? data.territories : [],
        field_locations: Array.isArray(data.field_locations) ? data.field_locations : [],
        office: data.office || {},
      };
      setSettings(next);
      onTerritoriesLoaded?.(next.territories);
    } catch (e) {
      setError(e.response?.data?.detail || "Unable to load attendance settings.");
    } finally {
      setLoading(false);
    }
  }, [onTerritoriesLoaded]);

  useEffect(() => { load(); }, [load]);

  const addTerritory = () => {
    const name = territory.trim();
    if (!name) return;
    if (settings.territories.some((item) => item.toLowerCase() === name.toLowerCase())) {
      setError("This territory already exists.");
      return;
    }
    setSettings((old) => ({
      ...old,
      territories: [...old.territories, name].sort((a, b) => a.localeCompare(b)),
    }));
    setTerritory("");
    setError("");
    setNotice("");
  };

  const states = Array.from(
    new Map(
      locationTree
        .filter((city) => city && city.state && city.parent_id)
        .map((city) => [city.parent_id, { id: city.parent_id, name: city.state }])
    ).values()
  ).sort((a, b) => a.name.localeCompare(b.name));

  const availableCities = locationTree.filter(
    (city) => city && city.type === "city" && city.parent_id === draft.state_id
  );
  const selectedCity = locationTree.find((city) => city.id === draft.city_id);
  const availableAreas = selectedCity?.areas || [];

  const addLocation = () => {
    const lat = Number(draft.latitude);
    const lon = Number(draft.longitude);
    const radiusKm = Number(draft.radius_km);
    const radius = Math.round(radiusKm * 1000);
    const selectedArea = availableAreas.find((area) => area.id === draft.area_id);

    if (!draft.name.trim() || !selectedArea || draft.latitude === "" || draft.longitude === "") {
      setError("Enter the location name, select an area, latitude and longitude.");
      return;
    }
    if (!Number.isFinite(lat) || lat < -90 || lat > 90 ||
        !Number.isFinite(lon) || lon < -180 || lon > 180 ||
        !Number.isFinite(radiusKm) || radiusKm < 0.01 || radiusKm > 5) {
      setError("Invalid coordinates or radius. Enter a radius between 0.01 and 5 km.");
      return;
    }

    const areaName = selectedArea.name;
    setSettings((old) => ({
      ...old,
      territories: old.territories.some(
        (item) => item.toLowerCase() === areaName.toLowerCase()
      ) ? old.territories : [...old.territories, areaName].sort((a, b) => a.localeCompare(b)),
      field_locations: [...old.field_locations, {
        id: `gps-${Date.now()}`,
        name: draft.name.trim(),
        area_id: selectedArea.id,
        area_name: areaName,
        city_id: selectedCity.id,
        city_name: selectedCity.name,
        state_id: selectedCity.parent_id,
        state_name: selectedCity.state,
        territory: areaName,
        latitude: lat,
        longitude: lon,
        radius_m: radius,
        active: true,
      }],
    }));
    setDraft({
      name: "", state_id: draft.state_id, city_id: draft.city_id,
      area_id: "", territory: areaName, latitude: "", longitude: "", radius_km: "0.1",
    });
    setError("");
    setNotice("");
  };

  const save = async () => {
    setSaving(true);
    setError("");
    setNotice("");
    try {
      const payload = {
        territories: settings.territories,
        field_locations: settings.field_locations,
        office: settings.office || {},
      };
      const { data } = await api.put("/admin/attendance/settings", payload);
      const saved = data.settings || payload;
      const next = {
        territories: saved.territories || payload.territories,
        field_locations: saved.field_locations || payload.field_locations,
        office: saved.office || payload.office,
      };
      setSettings(next);
      onTerritoriesLoaded?.(next.territories);
      setNotice("Territories and GPS locations saved successfully.");
    } catch (e) {
      setError(e.response?.data?.detail || "Unable to save attendance settings.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="space-y-5 rounded-2xl border bg-card p-4 sm:p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="flex items-center gap-2 text-lg font-bold">
            <MapPin className="h-5 w-5 text-violet-600" />
            Territory & GPS Location Management
          </h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Configure approved employee check-in and check-out locations.
          </p>
        </div>
        <Button type="button" variant="outline" onClick={load} disabled={loading}>
          <RefreshCw className="mr-2 h-4 w-4" /> Refresh
        </Button>
      </div>

      {error && <div role="alert" className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      {notice && <div role="status" className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700">{notice}</div>}

      <div className="grid gap-5 lg:grid-cols-2">
        <div className="space-y-3">
          <h4 className="font-semibold">Territories</h4>
          <div className="flex gap-2">
            <input className={inputClass} value={territory} onChange={(e) => setTerritory(e.target.value)} placeholder="e.g. Karimnagar" maxLength={150} />
            <Button type="button" onClick={addTerritory} disabled={!territory.trim()}>
              <Plus className="mr-1 h-4 w-4" /> Add
            </Button>
          </div>
          <div className="flex flex-wrap gap-2 rounded-xl border p-3">
            {settings.territories.length ? settings.territories.map((item) => (
              <span key={item} className="rounded-full border px-3 py-1.5 text-sm">{item}</span>
            )) : <span className="text-sm text-muted-foreground">No territories configured.</span>}
          </div>
          <p className="text-xs text-muted-foreground">Existing employee assignments are retained by the backend.</p>
        </div>

        <div className="space-y-3">
          <h4 className="font-semibold">Add approved GPS location</h4>
          <label className="block text-sm font-medium">Location name
            <input className={inputClass + " mt-1"} value={draft.name} onChange={(e) => setDraft((old) => ({ ...old, name: e.target.value }))} placeholder="e.g. Karimnagar Main Market" />
          </label>
          <label className="block text-sm font-medium">State
            <select className={inputClass + " mt-1"} value={draft.state_id}
              onChange={(e) => setDraft((old) => ({
                ...old, state_id: e.target.value, city_id: "", area_id: "",
              }))}>
              <option value="">Select state</option>
              {states.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
            </select>
          </label>
          <label className="block text-sm font-medium">City
            <select className={inputClass + " mt-1"} value={draft.city_id}
              disabled={!draft.state_id}
              onChange={(e) => setDraft((old) => ({ ...old, city_id: e.target.value, area_id: "" }))}>
              <option value="">Select city</option>
              {availableCities.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
            </select>
          </label>
          <label className="block text-sm font-medium">Area
            <select className={inputClass + " mt-1"} value={draft.area_id}
              disabled={!draft.city_id}
              onChange={(e) => setDraft((old) => ({ ...old, area_id: e.target.value }))}>
              <option value="">Select area</option>
              {availableAreas.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
            </select>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="text-sm font-medium">Latitude
              <input className={inputClass + " mt-1"} type="number" step="any" value={draft.latitude} onChange={(e) => setDraft((old) => ({ ...old, latitude: e.target.value }))} />
            </label>
            <label className="text-sm font-medium">Longitude
              <input className={inputClass + " mt-1"} type="number" step="any" value={draft.longitude} onChange={(e) => setDraft((old) => ({ ...old, longitude: e.target.value }))} />
            </label>
          </div>
          <label className="block text-sm font-medium">Allowed radius (kilometres)
            <input className={inputClass + " mt-1"} type="number" min="0.01" max="5" step="0.01"
              value={draft.radius_km}
              onChange={(e) => setDraft((old) => ({ ...old, radius_km: e.target.value }))} />
            <span className="mt-1 block text-xs text-muted-foreground">0.1 km = 100 metres.</span>
          </label>
          <Button type="button" onClick={addLocation} disabled={!availableAreas.length}>
            <Plus className="mr-2 h-4 w-4" /> Add GPS location
          </Button>
        </div>
      </div>

      <div className="space-y-3">
        <h4 className="font-semibold">Approved GPS locations ({settings.field_locations.length})</h4>
        {loading ? <p className="text-sm text-muted-foreground">Loading...</p> :
          settings.field_locations.length === 0 ? (
            <p className="rounded-xl border border-dashed p-4 text-center text-sm text-muted-foreground">No approved GPS locations configured.</p>
          ) : settings.field_locations.map((item) => (
            <div key={item.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border p-3">
              <div>
                <p className="font-semibold">{item.name || "Approved location"}</p>
                <p className="text-xs text-muted-foreground">
                  {item.state_name ? `${item.state_name} · ${item.city_name} · ${item.area_name || item.territory}` : item.territory}
                  {" · "}{item.latitude}, {item.longitude}
                  {" · Radius "}{(Number(item.radius_m || 100) / 1000).toFixed(2)} km
                </p>
                <span className={`text-xs font-medium ${item.active ? "text-emerald-700" : "text-slate-500"}`}>{item.active ? "Active" : "Inactive"}</span>
              </div>
              <div className="flex gap-2">
                <Button type="button" size="sm" variant="outline" onClick={() => setSettings((old) => ({
                  ...old,
                  field_locations: old.field_locations.map((loc) => loc.id === item.id ? { ...loc, active: !loc.active } : loc),
                }))}>{item.active ? "Deactivate" : "Activate"}</Button>
                <Button type="button" size="sm" variant="outline" onClick={() => {
                  if (window.confirm(`Remove ${item.name || "this GPS location"}?`)) {
                    setSettings((old) => ({ ...old, field_locations: old.field_locations.filter((loc) => loc.id !== item.id) }));
                  }
                }}><Trash2 className="mr-1 h-3.5 w-3.5" /> Remove</Button>
              </div>
            </div>
          ))}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 border-t pt-4">
        <p className="text-xs text-muted-foreground">Only active approved GPS locations in an employee's permanently or temporarily assigned areas are eligible for attendance.</p>
        <Button type="button" onClick={save} disabled={saving || loading}>
          {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving...</> : "Save Territory & GPS Settings"}
        </Button>
      </div>
    </section>
  );
}
